import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Grid,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import api from "../../utils/api";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const BillingSummarySection = () => {
  const [data, setData] = useState([]);
  const [typeMap, setTypeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("monthly");

  useEffect(() => {
    fetchBillingData();
    fetchWorkOrderTypes();
  }, []);

  const fetchBillingData = async () => {
    try {
      const response = await api.get("/jonas-exports");
      const result = response.data;

      if (Array.isArray(result)) {
        setData(result);
      } else if (Array.isArray(result.data)) {
        setData(result.data);
      } else {
        console.error("Unexpected billing data format:", result);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching billing data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkOrderTypes = async () => {
    try {
      const response = await api.get("/workorder-types");
      const types = response.data || [];
      const map = {};
      types.forEach((item) => {
        map[item.id] = item.type;
      });
      setTypeMap(map);
    } catch (error) {
      console.error("Error fetching work order types:", error);
    }
  };

  const handleViewModeChange = (_event, newValue) => {
    if (newValue) setViewMode(newValue);
  };

  const aggregateBillingByDate = () => {
    const grouped = {};

    data.forEach((item) => {
      if (!item.billable || !item.work_order_id || !item.sent_at) return;

      const dateKey =
        viewMode === "daily"
          ? dayjs(item.sent_at).format("YYYY-MM-DD")
          : dayjs(item.sent_at).format("YYYY-MM");

      if (!grouped[dateKey]) {
        grouped[dateKey] = 0;
      }

      grouped[dateKey] += parseFloat(item.price || 0);
    });

    const sortedKeys = Object.keys(grouped).sort();
    const labels = sortedKeys;
    const totals = sortedKeys.map((key) => grouped[key]);

    return { labels, totals };
  };

  const getBilledCountByType = () => {
    const counts = {};

    data.forEach((item) => {
      if (!item.billable || !item.work_order_id) return;

      const typeId = item.work_order_type;
      const typeName = typeMap[typeId] || "Unknown";

      if (!counts[typeName]) {
        counts[typeName] = 0;
      }
      counts[typeName]++;
    });

    const labels = Object.keys(counts).sort();
    const values = labels.map((label) => counts[label]);

    return { labels, values };
  };

  const { labels, totals } = useMemo(
    () => aggregateBillingByDate(),
    [data, viewMode]
  );

  const { labels: typeLabels, values: typeCounts } = useMemo(
    () => getBilledCountByType(),
    [data, typeMap]
  );

  const typeChartData = {
    labels: typeLabels,
    datasets: [
      {
        label: "Work Orders Billed",
        data: typeCounts,
        backgroundColor: "rgba(47,99,173,0.58)",
        borderColor: "rgba(47,99,173,0.88)",
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 22,
      },
    ],
  };

  const typeChartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.x} billed`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: "rgba(47,99,173,0.08)",
        },
        ticks: {
          precision: 0,
          stepSize: 1,
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: `Total Billing (${viewMode === "monthly" ? "Monthly" : "Daily"})`,
        data: totals,
        backgroundColor: "rgba(47,99,173,0.58)",
        borderColor: "rgba(47,99,173,0.88)",
        borderWidth: 1,
        borderRadius: 8,
        maxBarThickness: 34,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: (context) => `$${context.parsed.y.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(47,99,173,0.08)",
        },
        ticks: {
          callback: (value) => `$${value}`,
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 2, md: 2.5 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 0.5,
              }}
            >
              Billing View
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Switch between monthly and daily billing trends.
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                border: "1px solid rgba(47,99,173,0.12)",
                borderRadius: "10px !important",
                textTransform: "none",
                fontWeight: 600,
                color: "#5f6b7a",
                px: 2,
                minHeight: 38,
              },
              "& .MuiToggleButton-root.Mui-selected": {
                color: "#2f63ad",
                backgroundColor: "rgba(47,99,173,0.08)",
                borderColor: "rgba(47,99,173,0.18)",
              },
              "& .MuiToggleButton-root:hover": {
                backgroundColor: "rgba(47,99,173,0.04)",
              },
            }}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="daily">Daily</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Card>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              height: "100%",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  mb: 0.5,
                }}
              >
                Total Billing Amount
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                {viewMode === "monthly"
                  ? "Monthly billed totals over time."
                  : "Daily billed totals over time."}
              </Typography>
            </Box>

            <Box sx={{ height: 360 }}>
              {loading ? (
                <Typography sx={{ color: "#6b778c" }}>Loading...</Typography>
              ) : (
                <Bar data={chartData} options={chartOptions} />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              height: "100%",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  mb: 0.5,
                }}
              >
                Work Orders Billed by Type
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Count of billed work orders grouped by work order type.
              </Typography>
            </Box>

            <Box sx={{ height: 360 }}>
              {loading ? (
                <Typography sx={{ color: "#6b778c" }}>Loading...</Typography>
              ) : (
                <Bar data={typeChartData} options={typeChartOptions} />
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingSummarySection;
